(function ( $ ) {

    var seoSnippetHtmlTemplate = null;

    /**
     * A jQuery plugin that enables functionality related to SEO snippets. This plugin allows manipulation
     * or formatting of content to optimize it for search engine previews, such as titles, descriptions, or keywords.
     *
     * @function external:"jQuery.fn".seoSnippet
     * @param {Object} [options] - Optional settings for configuring the SEO snippet plugin behavior.
     * @param {number} [options.titleCharacterLimit] - Specifies the maximum character limit for the snippet content - meta title.
     * @param {number} [options.decriptionCharacterLimit] - Specifies the maximum character limit for the snippet content - meta description.
     * @param {boolean} [options.tinymce] - Specifies if the target description element uses TinyMCE editor
     * @returns {jQuery} The jQuery object for chainability.
     *
     * ### Example Usage:
     *
     * Initialize the plugin with optional settings:
     * ```javascript
     * $(document).ready(function () {
     *     $(".seo-snippet").seoSnippet({
     *         titleCharacterLimit: 60,
     *         descriptionCharacterLimit: 155,
     *         tinymce: true
     *     });
     * });
     * ```
     *
     * ### Required HTML Boilerplate:
     *
     * Structure your HTML with the following boilerplate for the plugin to function correctly:
     * ```html
     * <div class="seo-snippet"
     *      data-title-target="#title_1"
     *      data-description-target="#description_1"
     *      data-seoid-target="#seoid_1"
     *      data-url-template="https://www.domain.com/{URL}"
     *      data-title-template="{TITLE} - Category title - Project name">
     *
     *      <!-- Hidden input fields for meta data to be submitted to the backend -->
     *      <input type="hidden" name="meta_title_1" class="metaTitle" value="Your Meta Title">
     *      <input type="hidden" name="meta_description_1" class="metaDescription" value="Your Meta Description">
     *
     *      <!-- Example of dynamic binding targets -->
     *      <input type="text" id="title_1" value="Sample Title">
     *      <textarea id="description_1">Sample description for SEO preview</textarea>
     *      <input type="hidden" id="seoid_1" value="example-seo-id">
     * </div>
     * ```
     *
     * **Explanation of Attributes:**
     * - `data-title-target`: Selector for the element providing the `title` input.
     * - `data-description-target`: Selector for the element providing the `description` input.
     * - `data-seoid-target`: Selector for the element tracking the SEO ID.
     * - `data-url-template`: Template for generating preview URLs dynamically.
     * - `data-title-template`: Template for previewing the title with a custom format.
     *
     * The `metaTitle` and `metaDescription` hidden fields are required for backend submission.
     *
     * Ensure your HTML structure matches this boilerplate and that the target elements specified in
     * `data-title-target`, `data-description-target`, and `data-seoid-target` exist in the DOM, or the plugin
     * will terminate with a console error.
     */
    $.fn.seoSnippet = function( options ) {

        if (seoSnippetHtmlTemplate == null) loadTemplate();

        var defaults = {
            tinymce: false,
            titleCharacterLimit: 80,
            decriptionCharacterLimit: 160,
        };

        var settings = $.extend( {}, defaults, options );

        return this.each(function(){
            var parent = $(this);

            // Referencing elements
            var parentTitleElement = $(parent.data("titleTarget")); // data-title-target
            var parentDescriptionElement = $(parent.data("descriptionTarget"));
            var parentSeoIdElement = $(parent.data("seoidTarget"));

            // Hidden input elements, the ones that are submitted to be handled by backend
            var parentMetaTitleElement  = parent.find(".metaTitle");
            var parentMetaDescriptionElement  = parent.find(".metaDescription");

            if (parentTitleElement.length === 0 || parentDescriptionElement.length === 0 || parentSeoIdElement.length === 0) {
                console.error("Please provide a valid target element for the title, description and seoid");
                return;
            }

            if (parentMetaTitleElement.length === 0 || parentMetaDescriptionElement.length === 0) {
                console.error("Please provide a valid target element for the meta title and meta description - POST elements to be handled by backend");
                return;
            }

            // Append SEO Snippet HTML into the current wrapper
            parent.append(seoSnippetHtmlTemplate);

            // Referencing element values
            var parentTitleValue = parentTitleElement.val();
            var parentDescriptionValue = parentDescriptionElement.val();
            var parentSeoIdValue = parentSeoIdElement.val();

            // Title & URL Template
            var urlTemplate = parent.data("urlTemplate");
            var titleTemplate = parent.data("titleTemplate");

            // Snippet Elements
            var snippetMetaTitleElement = parent.find(".seo-snippet-meta-title").val(parentMetaTitleElement.val());
            var snippetMetaDescriptionElement = parent.find(".seo-snippet-meta-description").val(parentMetaDescriptionElement.val());

            var snippetMetaTitle = snippetMetaTitleElement.val();
            var snippetMetaDescription = snippetMetaDescriptionElement.val();

            var snippetPreviewTitleElement = parent.find(".seo-snippet-preview-title");
            var snippetPreviewUrlElement = parent.find(".seo-snippet-preview-url");
            var snippetPreviewDescriptionElement = parent.find(".seo-snippet-preview-description");

            addButtonEventListeners();
            addTargetElementEventListeners();
            updateSnippetMetaTitle();
            updateSnippetMetaDescription();
            updateSnippetUrl();

            /**
             * Adds event listeners to buttons for handling SEO snippet interactions,
             * such as editing the snippet, toggling the mobile view, and toggling the desktop view.
             *
             * @return {void} This function does not return anything.
             */
            function addButtonEventListeners(){

                parent.on("click", ".edit-snippet", function (e) {
                    e.preventDefault();
                    parent.find(".seo-snippet-content").slideToggle();
                    $(this).siblings().toggleClass("d-inline-block");
                });

                parent.on("click", ".mobile-view", function (e) {
                    e.preventDefault();
                    parent.find(".seo-snippet-content").addClass("mobile");
                    $(this).siblings().removeClass("active");
                    $(this).addClass("active");
                });
                parent.on("click", ".desktop-view", function (e) {
                    e.preventDefault();
                    parent.find(".seo-snippet-content").removeClass("mobile");
                    $(this).siblings().removeClass("active");
                    $(this).addClass("active");
                });
            }

            /**
             * Adds event listeners to targeted elements for handling keyup and change events.
             * Updates snippet values whenever the associated input elements are modified.
             *
             * @return {void} This method does not return any value.
             */
            function addTargetElementEventListeners(){

                parentTitleElement.on("keyup", function (e) {
                    parentTitleValue = $(this).val();
                    updateSnippetMetaTitle();
                });
                parentDescriptionElement.on("keyup", function (e) {
                    parentDescriptionValue = $(this).val();
                    updateSnippetMetaDescription();
                });
                parentSeoIdElement.on("keyup change", function (e) {
                    parentSeoIdValue = $(this).val();
                    updateSnippetUrl();
                });

                snippetMetaTitleElement.on("keyup change", function (e) {
                    snippetMetaTitle = $(this).val();
                    updateSnippetMetaTitle();
                });
                snippetMetaDescriptionElement.on("keyup change", function (e) {
                    snippetMetaDescription = $(this).val();
                    updateSnippetMetaDescription();
                });
            }

            function updateSnippetMetaTitle(){
                let metaTitle = snippetMetaTitle != "" ? snippetMetaTitle : titleTemplate.replace("{TITLE}", parentTitleValue);
                metaTitle = cleanString(metaTitle, settings.titleCharacterLimit);
                snippetPreviewTitleElement.text(metaTitle);
                parentMetaTitleElement.val(snippetMetaTitle);
            }

            function updateSnippetMetaDescription(){
                let metaDescription = snippetMetaDescription != "" ? snippetMetaDescription : parentDescriptionValue;
                metaDescription = cleanString(metaDescription, settings.decriptionCharacterLimit);
                snippetPreviewDescriptionElement.text(metaDescription);
                parentMetaDescriptionElement.val(snippetMetaDescription);
            }

            function updateSnippetUrl(){
                let url = urlTemplate.replace("{URL}", parentSeoIdValue);
                snippetPreviewUrlElement.text(url);
            }
        });


        /**
         * Cleans a string to make it meta-tag safe.
         * This includes removing special characters, extra spaces, and ensuring it adheres to SEO practices.
         *
         * @param {string} str The input string to be cleaned.
         * @returns {string} Meta-tag safe string.
         */
        function cleanString(str, limit = 160) {
            if (!str || typeof str !== 'string') {
                return '';
            }

            // Trim whitespace from the start and end
            let cleanStr = str.trim();

            // Replace multiple spaces with a single space
            cleanStr = cleanStr.replace(/\s+/g, ' ');

            // Remove special characters (except for common text symbols like -.!)
            cleanStr = cleanStr.replace(/[^\p{L}\p{N}\-_.! ]/gu, '');

            // Limit the length
            cleanStr = cleanStr.substring(0, limit);

            return cleanStr;
        }

        function loadTemplate(){
            seoSnippetHtmlTemplate = `
                <div class="seo-snippet-body">
                    <div class="seo-snippet-buttons">
                        <label class="form-label">Modify SEO text / Google preview</label>
                        <div class="form-group">
                            <button class="btn btn-sm  btn-secondary mobile-view" type="button">
                                <i class="bi bi-phone"></i> Mobile
                            </button>
                            <button class="btn btn-sm  btn-secondary desktop-view" type="button">
                                <i class="bi bi-display"></i> Desktop
                            </button>
                            <button class="btn btn-sm btn-secondary edit-snippet" type="button">
                                <span class="glyphicon glyphicon-pencil"></span> SEO snippet preview
                            </button>
                        </div>
                    </div>
                    <div class="seo-snippet-content">
                        <div class="inputs-wrap">
                            <div class="form-group">
                                <label class="form-label">META Title</label>
                                <input type="text" class="form-control form-control-sm seo-snippet-meta-title" name="meta_title" value=""
                                       maxlength="255">
                            </div>
                            <div class="form-group">
                                <label class="form-label">META Description</label>
                                <textarea rows="2" class="form-control form-control-sm seo-snippet-meta-description" name="meta-description"></textarea>
                            </div>
                        </div>
                        <div class="seo-snippet-preview">
                            <h3 class="seo-snippet-preview-title"></h3>
                            <p class="seo-snippet-preview-url"></p>
                            <p class="seo-snippet-preview-description"></p>
                        </div>
                    </div>
                </div>`;
        }
    };

}( jQuery ));
